import create from 'zustand';
import produce from 'immer';
import { devtools } from 'zustand/middleware';
import { CommonState, Option, ToastMessage } from '../types/common.d';
import { IhostspotRequest, IRotationRequest } from '../types/hotspot';
import { VisualQCStatus } from '../utils/enums';

const store = (set): CommonState => ({
    loading: false,
    isLoggedIn: false,
    userName: '',
    sideBarOpen: false,
    flyoutRightOpen: false,
    toastMessage: null,
    alwaysHideFeedback: false,
    isProductFlagged: false,
    downloadProgress: { fileCount: 0, totalFiles: 0, requestName: '' },
    showDownloadDropdown: false,
    goLiveClicked: false,
    pushDataToProd: false,
    clearHotspotConfig: false,
    hotspotCoordinates: null,
    rotationConfig: null,
    isHotspotCreationActive: false,
    activeHostpot: '',
    isUploadInitiated: false,
    currentRequestId: '',
    modelUploadApiResponse: [],
    threeDLiveFsns: [],
    goLiveFsns: [],
    activeFSN: '',
    setLoading: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.loading = value;
            })
        );
    },
    setSideBarOpen: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.sideBarOpen = value;
            })
        );
    },
    setFlyoutRightOpen: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.flyoutRightOpen = value;
            })
        );
    },
    setToastMessage: (value: ToastMessage | null, timeout: number = 4000, onClose?: Function) => {
        set(
            produce((state: CommonState) => {
                state.toastMessage = value;
            })
        );
        setTimeout(() => {
            set(
                produce((state: CommonState) => {
                    state.toastMessage = null;
                })
            );
            onClose && onClose();
        }, timeout);
    },
    setProductFlagged: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.isProductFlagged = value;
            })
        );
        setTimeout(() => {
            set(
                produce((state: CommonState) => {
                    state.isProductFlagged = value;
                })
            );
        }, 1000);
    },
    setAlwaysHideFeedback: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.alwaysHideFeedback = value;
            })
        );
    },
    setDownloadProgress: (count: number, totalFiles?: number, request?: string) => {
        set(
            produce((state: CommonState) => {
                state.downloadProgress.fileCount = count;
                state.downloadProgress.totalFiles = totalFiles;
                state.downloadProgress.requestName = request;
            })
        );
    },
    setShowDownloadDropdown: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.showDownloadDropdown = value;
            })
        );
    },
    setGoLiveClicked: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.goLiveClicked = value;
            })
        );
    },
    setPushDataToProd: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.pushDataToProd = value;
            })
        );
    },
    setClearHotspotConfig: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.clearHotspotConfig = value;
            })
        );
    },
    setHotspotCoordinate: (value: IhostspotRequest) => {
        set(
            produce((state: CommonState) => {
                state.hotspotCoordinates = value;
            })
        );
    },
    setHotspotCreation: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.isHotspotCreationActive = value;
            })
        );
    },
    setActiveHostpot: (value: string) => {
        set(
            produce((state: CommonState) => {
                state.activeHostpot = value;
            })
        );
    },
    setRotationConfig: (value: IRotationRequest) => {
        set(
            produce((state: CommonState) => {
                state.rotationConfig = value;
            })
        );
    },
    setActiveFSN: (value: string) => {
        set(
            produce((state: CommonState) => {
                state.activeFSN = value;
            })
        );
    },
    setIsUploadInitiated: (value: boolean) => {
        set(
            produce((state: CommonState) => {
                state.isUploadInitiated = value;
            })
        );
    },

    setCurrentRequestId: (value: string) => {
        set(
            produce((state: CommonState) => {
                state.currentRequestId = value;
            })
        );
    },
    setThreeDLiveFsns: (data) => {
        set(
            produce((state: CommonState) => {
                state.threeDLiveFsns = data;
            })
        );
    },
    setGoLiveFsns: (data) => {
        set(
            produce((state: CommonState) => {
                state.goLiveFsns = data;
            })
        );
    },
    setModelUploadApiResponse: (data) => {
        set(
            produce((state: CommonState) => {
                state.modelUploadApiResponse = data;
            })
        );
    },
    setVisualQCStatus: (fsnToToggle: string, value: VisualQCStatus) => {
        set(
            produce((state: CommonState) => {
                if (state.threeDLiveFsns) {
                    const itemIndex = state.threeDLiveFsns.findIndex((item) => item.fsn === fsnToToggle);

                    if (itemIndex !== -1) {
                        state.threeDLiveFsns[itemIndex].visualQCStatus = value;
                    }
                }
            })
        );
    },
    setFSNModelMetaData: (name: string, value: Option | boolean) => {
        set(
            produce((state: CommonState) => {
                if (state.goLiveFsns) {
                    state.goLiveFsns.forEach((item) => {
                        if (item.arInfoData) {
                            if (name === 'arInfoData') {
                                item.arInfoData = value as Option;
                            } else {
                                item.arInfoData.meta[name].value = value as boolean;
                            }
                        }
                    });
                }
            })
        );
    }
});

export const useCommonStore = create<CommonState>(devtools(store));
